"use client";

import { trackEmailComposerAction } from "../analytics";
import { useEmailComposer } from "../context";
import { getMarkRange, getMarkType } from "@tiptap/core";
import { useCallback, useRef, useState } from "react";
import { LuExternalLink, LuLink, LuLink2Off, LuPencil, LuType } from "react-icons/lu";
import { Button, Input } from "@/components/ui";
import { LinkMenuState } from "./link-bubble-menu-handler";

/**
 * Ensures a URL has a protocol. Adds https:// if missing.
 * Preserves mailto:, tel:, and other explicit protocols.
 */
function normalizeHref(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return trimmed;
  // Block javascript: and data: protocols
  if (/^(javascript|data):/i.test(trimmed)) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}
interface LinkEditorProps {
  /** Current mode driven by LinkBubbleMenuHandler state. */
  mode: LinkMenuState;
}

/**
 * Inline link editor used by the bubble menu.
 * Renders VIEW (url preview + actions) or EDIT (input form) based on
 * the LinkBubbleMenuHandler extension state.
 *
 * The editor selection is the source of truth for which link is targeted.
 */
export function LinkEditor({
  mode
}: LinkEditorProps) {
  const {
    editor
  } = useEmailComposer();
  const currentHref = editor?.isActive("link") ? editor.getAttributes("link").href as string ?? "" : "";
  const getSelectedText = () => editor?.state.selection.empty ? "" : editor?.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, "") ?? "";
  const [url, setUrl] = useState(currentHref);
  const [text, setText] = useState(getSelectedText);
  const urlInputRef = useRef<HTMLInputElement>(null);

  // Sync state when entering EDIT mode
  const prevModeRef = useRef<LinkMenuState | null>(null);
  if (mode === LinkMenuState.EDIT && prevModeRef.current !== LinkMenuState.EDIT) {
    setUrl(currentHref);
    setText(getSelectedText());
  }
  prevModeRef.current = mode;
  const applyLink = useCallback(() => {
    if (!editor) return;
    const href = normalizeHref(url);
    if (href === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const linkType = getMarkType("link", editor.schema);
      const range = getMarkRange(editor.state.selection.$from, linkType);
      if (range) {
        // Existing link — update text if changed, then set href
        const existingText = editor.state.doc.textBetween(range.from, range.to, "");
        if (text && text !== existingText) {
          editor.chain().focus().extendMarkRange("link").command(({
            tr,
            dispatch
          }) => {
            if (dispatch) {
              tr.insertText(text, range.from, range.to);
            }
            return true;
          }).setTextSelection({
            from: range.from,
            to: range.from + text.length
          }).setLink({
            href
          }).run();
        } else {
          editor.chain().focus().extendMarkRange("link").setLink({
            href
          }).run();
        }
      } else if (text) {
        // No existing link, text provided — insert text with link
        const {
          from,
          to
        } = editor.state.selection;
        editor.chain().focus().command(({
          tr: tr_0,
          dispatch: dispatch_0
        }) => {
          if (dispatch_0) {
            tr_0.insertText(text, from, to);
          }
          return true;
        }).setTextSelection({
          from,
          to: from + text.length
        }).setLink({
          href
        }).run();
      } else {
        // No text, just apply link to selection
        editor.chain().focus().setLink({
          href
        }).run();
      }
    }
    trackEmailComposerAction("link_applied", "bubble_menu");
    editor.commands.closeLinkBubbleMenu();
  }, [editor, url, text]);
  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    trackEmailComposerAction("link_removed", "bubble_menu");
    editor.commands.closeLinkBubbleMenu();
  }, [editor]);
  const handleEscape = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      editor?.commands.closeLinkBubbleMenu();
      editor?.commands.focus();
    }
  }, [editor]);
  if (!editor) return null;
  if (mode === LinkMenuState.EDIT) {
    return <form onSubmit={e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      applyLink();
    }} className="flex flex-col gap-1.5 w-64">
        <div className="flex items-center gap-1.5">
          <LuType size={14} className="shrink-0 text-foreground-secondary" />
          <Input value={text} onChange={e_1 => setText(e_1.target.value)} onKeyDown={handleEscape} placeholder="Text" className="h-7 text-caption" />
        </div>
        <div className="flex items-center gap-1.5">
          <LuLink size={14} className="shrink-0 text-foreground-secondary" />
          <Input ref={urlInputRef} autoFocus value={url} onChange={e_2 => setUrl(e_2.target.value)} onKeyDown={handleEscape} placeholder="Search or paste a link" className="h-7 text-caption" />
          <Button type="submit" variant="ghost" size="sm" className="h-7 shrink-0 text-caption text-foreground-secondary">
            Apply
          </Button>
        </div>
      </form>;
  }

  // VIEW mode
  return <div className="flex items-center gap-1">
      <span className="max-w-48 truncate text-foreground-secondary text-caption cursor-default" onMouseDown={e_3 => e_3.preventDefault()}>
        {currentHref}
      </span>
      <Button type="button" variant="ghost" size="icon-sm" className="shrink-0 size-7" onMouseDown={e_4 => e_4.preventDefault()} onClick={() => {
      window.open(currentHref, "_blank", "noopener,noreferrer");
      trackEmailComposerAction("link_opened_external", "bubble_menu");
    }} tooltip="Open link">
        <LuExternalLink size={13} />
      </Button>
      <Button type="button" variant="ghost" size="icon-sm" className="shrink-0 size-7" onMouseDown={e_5 => e_5.preventDefault()} onClick={() => {
      editor.commands.editLinkInBubbleMenu();
      trackEmailComposerAction("link_edit_started", "bubble_menu");
    }} tooltip="Edit link">
        <LuPencil size={13} />
      </Button>
      <Button type="button" variant="ghost" size="icon-sm" className="shrink-0 size-7" onMouseDown={e_6 => e_6.preventDefault()} onClick={removeLink} tooltip="Remove link">
        <LuLink2Off size={13} />
      </Button>
    </div>;
}